import { useState } from 'react';
import { Plus, ChevronRight } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { useCategories } from '@/hooks/use-categories';
import { CategoryCreateModal } from './CategoryCreateModal';
import { cn } from '@/lib/utils';

interface CategorySelectProps {
  value: string | null;
  onChange: (categoryId: string, categoryName: string) => void;
  type: 'income' | 'expense';
  placeholder?: string;
  allowCreate?: boolean;
  className?: string;
  triggerClassName?: string;
}

export function CategorySelect({
  value,
  onChange,
  type,
  placeholder = 'Selecionar categoria...',
  allowCreate = true,
  className,
  triggerClassName
}: CategorySelectProps) {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const { categories, hierarchicalCategories, categoryMap, createCategory, refetch } = useCategories(type);

  const selectedCategory = value ? categoryMap.get(value) : null;

  const handleCreate = async (data: Parameters<typeof createCategory>[0]) => {
    const result = await createCategory(data);
    if (result) {
      setCreateModalOpen(false);
      onChange(result.id, result.name);
      refetch();
    }
    return result;
  };

  // Build display list with hierarchy
  const displayItems: { id: string; name: string; isChild: boolean; parentName?: string }[] = [];
  
  hierarchicalCategories.forEach(parent => {
    displayItems.push({ id: parent.id, name: parent.name, isChild: false });
    parent.children.forEach(child => {
      displayItems.push({ 
        id: child.id, 
        name: child.name, 
        isChild: true, 
        parentName: parent.name 
      });
    });
  });

  // Add orphan categories (those with parent_id that don't match any parent)
  categories
    .filter(c => c.parent_id && !hierarchicalCategories.find(p => p.id === c.parent_id))
    .forEach(orphan => {
      if (!displayItems.find(d => d.id === orphan.id)) {
        displayItems.push({ id: orphan.id, name: orphan.name, isChild: false });
      }
    });

  return (
    <div className={className}>
      <Select
        value={value || ''}
        onValueChange={(val) => {
          if (val === '__create__') {
            setCreateModalOpen(true);
            return;
          }
          const cat = categoryMap.get(val);
          if (cat) onChange(val, cat.name);
        }}
      >
        <SelectTrigger className={triggerClassName}>
          <SelectValue placeholder={placeholder}>
            {selectedCategory?.name || placeholder}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {allowCreate && (
            <>
              <SelectItem value="__create__" className="text-primary font-medium">
                <span className="flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  Criar Nova Categoria
                </span>
              </SelectItem>
              <Separator className="my-1" />
            </>
          )}
          
          {displayItems.map(item => (
            <SelectItem 
              key={item.id} 
              value={item.id}
              className={cn(item.isChild && 'pl-6')}
            >
              <span className="flex items-center gap-1">
                {item.isChild && (
                  <ChevronRight className="h-3 w-3 text-muted-foreground" />
                )}
                {item.name}
              </span>
            </SelectItem>
          ))}

          {displayItems.length === 0 && (
            <div className="py-2 px-3 text-sm text-muted-foreground text-center">
              Nenhuma categoria encontrada
            </div>
          )}
        </SelectContent>
      </Select>

      <CategoryCreateModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        type={type}
        parentCategories={hierarchicalCategories}
        onCreate={handleCreate}
      />
    </div>
  );
}
